import * as React from 'react';
import { FieldProps } from '../metadataFormField';
import { getDefaultDataKey, getFieldValue, getSchema } from '../../../utils';
import { NodeType } from '../../../enums/NodeType';
import { Pill, Pills } from '../../Pill/pill';
import { FieldGroup } from '../FieldGroup/fieldGroup';
import { ReferenceSearch } from '../../ReferenceSearch/referenceSearch';
import { ImpliedData } from '../ImpliedData/impliedData';
import { ValidationResponse } from '../../../types/ValidationResponse';
import { SeeOption } from '../../../enums/SeeOption';
import { useSelector } from '../../../utils/useSelector';
import { API } from '../../../api';
import { Metadata, WorkspaceSetting } from '../../../types/Metadata';

const getRefIds = (list: Metadata[]) =>
  list?.map(l => l?.props?.refId).filter(x => x);

const useSearchAvailability = (
  api: Pick<API, 'searchReferences'> | null,
  nodeType: string,
  enabled: boolean,
  initialValue = false
): boolean => {
  const [hasOptions, setHasOptions] = React.useState(initialValue);

  React.useEffect(() => {
    if (!enabled || !api) return;

    api
      .searchReferences({
        nodeType: nodeType as NodeType,
        q: '',
        maxResults: 1
      })
      .then(results => {
        setHasOptions(!!results?.length);
      })
      .catch(() => {
        setHasOptions(false);
      });
  }, [api, enabled]);

  return hasOptions;
};

export const ReferenceField = (props: FieldProps) => {
  const { field, data, disabled, updater, children } = props;
  const { updateField, update } = updater;
  const getAPI = useSelector(s => s.app?.elFn?.getAPI);
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const isMyItemsView = useSelector(s => s.view?.selected?.isMyItemsView);
  const workspaceSettings = useSelector(
    s => s.planning?.selectedWorkspace.setting
  );
  const value = getFieldValue(field, data);
  const schema = getSchema(schemas, field.dataType as NodeType);
  const includeIds =
    field?.key == 'Item.status' ? [`status:1`, `status:10`] : null;
  const shouldMask = !!isMyItemsView && field.dataType == NodeType.Employee;

  const { api, seeOptions } = React.useMemo(() => {
    const dataType = field.dataType;
    if (field?.key == 'Item.pilotType') {
      return getPilotTypeSearchAPI(getAPI(), data);
    }
    if (dataType == NodeType.UseCase) {
      return getUseCaseSearchAPI(getAPI(), workspaceSettings, data);
    }
    const defAPI = getDefaultDataAPI(dataType, getAPI(), workspaceSettings);
    return defAPI || { api: null, seeOptions: null };
  }, [
    workspaceSettings?.props?.showTSPUseCases,
    data?.props?.function,
    data?.props?.contentType,
    workspaceSettings,
    field?.key
  ]);
  const pillsClassName =
    field.dataType == NodeType.Conference ? 'pills-multi' : null;
  const isPilotType = field?.key === 'Item.pilotType';
  const isPilotTypeAvailable = useSearchAvailability(
    api,
    field.dataType,
    isPilotType,
    isPilotType && !!value?.length
  );

  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (isPilotType && value?.length) {
      updater.updateField(field, []);
    }
  }, [data?.props?.contentType]);

  if (isPilotType && !isPilotTypeAvailable) return null;

  return (
    <FieldGroup
      {...props}
      disabled={disabled || !schema}
      value={
        !value?.length ? null : (
          <Pills
            commaSeparated
            shouldMask={shouldMask}
            className={pillsClassName}
          >
            {value?.map((f, idx) => {
              if (!schema?.props?.displayField) return null;
              let v = f.props[schema.props.displayField];
              return <Pill key={idx} name={v} data={f} />;
            })}
          </Pills>
        )
      }
    >
      {(vr: ValidationResponse) => (
        <>
          <ReferenceSearch
            onChange={values => updateField(field, values)}
            api={api}
            seeOptions={seeOptions}
            onBlur={update}
            nodeType={schema?.props?.type}
            multiple={field.multiValue}
            maxResults={50}
            minLength={0}
            selected={value}
            includeIds={includeIds}
            getClassName={o => {
              if (!vr) return `${shouldMask ? 'fs-mask' : null}`;
              if (
                vr.errors?.filter(e => e?.refIds?.includes(o?.props?.refId))
                  ?.length
              )
                return `error ${shouldMask ? 'fs-mask' : ''}`;
            }}
            shouldMask={shouldMask}
            schemaField={field}
            excludeRecords={
              [
                'DisrupterTopic.reviewer',
                'DisrupterTopic.additionalReviewers'
              ]?.includes(field.key)
                ? [
                    ...(data?.props?.reviewer || []),
                    ...(data?.props?.additionalReviewers || [])
                  ]
                : null
            }
          />
          <ImpliedData {...props} schemas={schemas} />
          {children}
        </>
      )}
    </FieldGroup>
  );
};

export const getPilotTypeSearchAPI = (
  { searchReferences }: API,
  data?: Partial<Metadata>
): { seeOptions: SeeOption[]; api: Pick<API, 'searchReferences'> } => {
  const filterByContentTypeIds = getRefIds(data?.props.contentType) || [];
  const api: Pick<API, 'searchReferences'> = {
    searchReferences: async payload => {
      if (!filterByContentTypeIds.length) return [];
      payload.filterByContentTypeIds = filterByContentTypeIds;
      const results = await searchReferences(payload);
      return results;
    }
  };
  return { api, seeOptions: null };
};

export const getUseCaseSearchAPI = (
  { searchReferences }: API,
  ws: WorkspaceSetting,
  data?: Partial<Metadata>
): { seeOptions: SeeOption[]; api: Pick<API, 'searchReferences'> } => {
  const seeOptions: SeeOption[] = [];
  const hasPrimaryFunction = data?.props?.function?.length;
  const hasPrimaryTSP = data?.props?.function?.filter(
    k => k?.props?.refId == 'function:10943'
  ).length;
  const showTSPUseCases = ws?.props?.showTSPUseCases;
  if (!hasPrimaryFunction) {
    if (!showTSPUseCases) {
      seeOptions.push(SeeOption.NonTSPUseCases);
      seeOptions.push(SeeOption.TSPUseCases);
    } else {
      seeOptions.push(SeeOption.TSPUseCases);
      seeOptions.push(SeeOption.NonTSPUseCases);
    }
  } else {
    if (hasPrimaryTSP) {
      seeOptions.push(SeeOption.TSPUseCases);
    } else {
      seeOptions.push(SeeOption.NonTSPUseCases);
    }
  }
  const api: Pick<API, 'searchReferences'> = {
    searchReferences: async payload => {
      const results = await searchReferences(payload);
      if (payload.seeOption === undefined) return results;
      let seeOpt = payload.seeOption;
      if (hasPrimaryFunction) {
        seeOpt = hasPrimaryTSP
          ? SeeOption.TSPUseCases
          : SeeOption.NonTSPUseCases;
      } else {
        if (!seeOpt) {
          seeOpt = showTSPUseCases
            ? SeeOption.TSPUseCases
            : SeeOption.NonTSPUseCases;
        }
      }
      return results?.filter(r => {
        if (r?.props?.refId == 'blanks') return true;
        return (
          (seeOpt == SeeOption.TSPUseCases && !!r?.props?.isTSP) ||
          (seeOpt != SeeOption.TSPUseCases && !r?.props?.isTSP)
        );
      });
    }
  };
  return { api, seeOptions };
};

export const getDefaultDataAPI = (
  nodeType: string,
  { searchReferences }: API,
  ws: WorkspaceSetting,
  itemType?: string
): { seeOptions: SeeOption[]; api: Pick<API, 'searchReferences'> } => {
  if (!nodeType || !ws?.props) return null;
  const key = getDefaultDataKey(nodeType);
  const defaultData = ws.props[key] as string[];
  if (!defaultData?.length && (nodeType != NodeType.Status || !itemType)) {
    return null;
  }
  const api: Pick<API, 'searchReferences'> = {
    searchReferences: payload => {
      if (payload.seeOption != SeeOption.All) {
        if (!!defaultData) {
          payload.limitToIds = defaultData;
        }
        if (nodeType == NodeType.Status && !!itemType) {
          payload.itemType = itemType;
        }
      }
      return searchReferences(payload);
    }
  };
  return { api, seeOptions: [SeeOption.Less, SeeOption.All] };
};
